import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetDeliveryOrderByIdObject } from "../../../@types/DRO/DeliveryOrderResponse";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";

interface DeliveryOrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderId: number;
}

const DeliveryOrderHistoryModal: React.FC<DeliveryOrderHistoryModalProps> = ({
  isOpen,
  onClose,
  purchaseOrderId,
}) => {
  const navigate = useNavigate();
  const [deliveryOrders, setDeliveryOrders] = useState<
    GetDeliveryOrderByIdObject[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && purchaseOrderId) {
      fetchDeliveryOrders();
    }
  }, [isOpen, purchaseOrderId]);

  const fetchDeliveryOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DeliveryOrderApi.api.getAllDeliveryOrderByPOId(
        purchaseOrderId
      );
      if (response.data && response.data.deliveryOrders) {
        setDeliveryOrders(response.data.deliveryOrders);
      } else {
        setDeliveryOrders([]);
      }
    } catch (err) {
      setError("Failed to fetch delivery orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: { name: string; displayName: string }) => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DRAFT: {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: status?.displayName || "Draft",
      },
      CREATED: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: status?.displayName || "Created",
      },
      COMPLETED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: status?.displayName || "Completed",
      },
      IN_TRANSIT: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: status?.displayName || "In Transit",
      },
      DELIVERED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: status?.displayName || "Delivered",
      },
      CANCELLED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: status?.displayName || "Cancelled",
      },
    };
    return (
      statusMap[status?.name] || {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: status?.displayName || "Unknown",
      }
    );
  };

  const handleRowClick = (orderId: number) => {
    navigate(`/delivery-order/details/${orderId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity"
          aria-hidden="true"
          onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Delivery Order History - PO #{purchaseOrderId}
                </h3>

                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : error ? (
                  <div className="text-red-600 text-center py-8">{error}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                          <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="w-40 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reference
                          </th>
                          <th className="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vendor
                          </th>
                          <th className="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Warehouse
                          </th>
                          <th className="w-40 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Creation Date
                          </th>
                          <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {deliveryOrders.length > 0 ? (
                          deliveryOrders.map((order, index) => {
                            const statusInfo = getStatusBadge(order.status);
                            const isHighlighted =
                              order.status?.name === "COMPLETED";

                            return (
                              <tr
                                key={order.id}
                                onClick={() => handleRowClick(order.id)}
                                className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                                  isHighlighted
                                    ? "bg-blue-50 border-l-4 border-l-blue-500"
                                    : ""
                                }`}>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {index + 1}
                                </td>
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                  {order.id}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {order.referenceCode || "N/A"}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {order.vendorWarehouseDto?.vendorName ||
                                    "N/A"}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {order.deliveryWareHouseDetails
                                    ?.deliveryWarehouseName || "N/A"}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {order.creationDate}
                                </td>
                                <td className="px-4 py-4">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                                    {statusInfo.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={7}
                              className="px-4 py-12 text-center text-gray-500">
                              <div className="flex flex-col items-center">
                                <svg
                                  className="w-12 h-12 text-gray-300 mb-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                  />
                                </svg>
                                <p className="text-lg font-medium text-gray-900 mb-1">
                                  No delivery orders found
                                </p>
                                <p className="text-gray-500">
                                  No delivery orders have been created for this
                                  purchase order yet.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOrderHistoryModal;
